import { Box, Button, Modal, SelectInput, TextInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { SyntheticEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { PropertyType, propertyTypeLabelsMap } from 'types';

import { useCreateOrganizationPropertyTypePricingMutation } from '../../../../../../services/pricing';

const propertyTypeOptions = Object.values(PropertyType).map((value) => ({
  label: propertyTypeLabelsMap[value],
  value,
}));

export const CreatePropertyType = ({
  isModalOpen,
  closeModal,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
}) => {
  const [createPropertyTypePricing] = useCreateOrganizationPropertyTypePricingMutation();
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();

  const formik = useFormik({
    initialValues: {
      propertyType: null as { label: string; value: PropertyType } | null,
      amount: '' as string,
    },
    validationSchema: yup.object({
      propertyType: yup.object().nullable().required('Property Type is required'),
      amount: yup
        .number()
        .typeError('Amount must be a number')
        .positive('Amount must be greater than 0')
        .required('Amount is required'),
    }),
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        await createPropertyTypePricing({
          alias,
          data: {
            propertyType: values.propertyType.value,
            pricePerWatt: Number(values.amount),
            programType,
          },
        }).unwrap();
        toast.success('Property Type pricing added successfully');
        resetForm();
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error adding Property Type pricing');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal ariaLabelledBy="createPropertyType" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createPropertyType" title="Add Property Type Pricing" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary">
          <Box direction="column" childGap="xl">
            <SelectInput
              id="propertyType"
              name="propertyType"
              label="Property Type"
              value={formik.values.propertyType}
              onChange={(event) => formik.setFieldValue('propertyType', event.target.value)}
              options={propertyTypeOptions}
              onBlur={formik.handleBlur}
              isDisabled={formik.isSubmitting}
              error={formik.touched.propertyType && (formik.errors.propertyType as string)}
              menuPortalTarget={document.body}
              placeholder="e.g. Condo"
              isRequired
            />
            <TextInput
              type="number"
              label="Amount"
              name="amount"
              id="amount"
              value={formik.values.amount}
              onChange={(event) => formik.setFieldValue('amount', event.target.value)}
              onBlur={formik.handleBlur}
              prefix="$"
              suffix="/W"
              step="0.01"
              error={formik.touched.amount && (formik.errors.amount as string)}
              isRequired
            />
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            type="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
            Add Property Type
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
